/**
 * `<AnalysisLoadedView>` — output-presentation root for analysis-loaded state (B-13).
 *
 * Renders when inner state machine resolves to `analysis-loaded`.
 *
 * B-13 Δ1 (BLOCKER fix):
 *   - Dropped `actionBar` slot (architectural drift source).
 *   - Now renders `<AnalysisGenerationBar>` internally.
 *   - Accepts `jdId`, `jds`, `onJdChange`, `onReplaceTranscript`,
 *     `rankBefore`, `rankAfter` from caller.
 *   - Passes rank props through to `<AnalysisCards>` → `<VerdictCard>` → `<RankDeltaBadge>`.
 *
 * Component tree (prototype L1573-1583 + design §1):
 *   <AnalysisGenerationBar/>   — JD select + Replace Transcript ghost (at TOP)
 *   <ConversationHealthBar/>   — model-derived conversation tone/rapport signal
 *                                (renders only when the analysis carries one)
 *   <AnalysisCards/>           — four-card output stack (with rank props)
 *
 * Regenerate removed (auto-eval-prep): the analysis artifact is deterministic.
 * `isRegenerating`, `onGenerateOrRegenerate`, and `regenModal` slots are gone.
 * The `analysis-regenerating` inner state has also been removed.
 */
import type { JSX } from "react";
import { ConversationHealthBar } from "./ConversationHealthBar";
import { AnalysisCards } from "./AnalysisCards";
import { AnalysisGenerationBar } from "./AnalysisGenerationBar";
import { AiRatingWidget } from "@/components/ai/AiRatingWidget";

interface JdSummary {
  id: string;
  name: string;
}

interface AnalysisLoadedViewProps {
  /** The analysis markdown string. Required — callers only render this when non-null. */
  analysis: string;
  /** Transcript text — drives the gen-bar's "Replace Transcript" affordance. */
  transcript: string | null | undefined;

  /** JD id for the generation-context selector in the gen-bar. */
  jdId?: string | undefined;
  /** Available JDs for the gen-bar select. */
  jds?: JdSummary[];
  /** Called when gen-bar JD select changes. */
  onJdChange?: (jdId: string | undefined) => void;
  /** Called when Replace Transcript ghost button is clicked. */
  onReplaceTranscript?: () => void;
  /** Whether a mutation is in flight (disables gen-bar buttons). */
  isPending?: boolean;

  /** Rank before (forwarded to RankDeltaBadge in VerdictCard). */
  rankBefore?: number | null;
  /** Rank after (forwarded to RankDeltaBadge in VerdictCard). */
  rankAfter?: number | null;

  /** Analysis row id — feeds the `AiRatingWidget` resourceKey. */
  analysisId?: string;
  /**
   * Model that produced this analysis — gates the `AiRatingWidget` (renders
   * nothing when null). Null on the freshly-submitted paste-box path until the
   * complete row refetches.
   */
  modelUsed?: string | null;
}

export function AnalysisLoadedView({
  analysis,
  transcript,
  jdId,
  jds,
  onJdChange,
  onReplaceTranscript,
  isPending = false,
  rankBefore,
  rankAfter,
  analysisId,
  modelUsed = null,
}: AnalysisLoadedViewProps): JSX.Element {
  return (
    <div
      className="flex flex-col gap-4 px-content-px py-5"
      data-testid="analysis-loaded-view"
    >
      {/*
       * Generation bar — at TOP per prototype L1573 (gen-bar above sentiment + cards).
       * Always rendered in analysis-loaded state. hasAnalysis=true hides the
       * generate CTA (artifact is deterministic — no re-run affordance).
       */}
      <AnalysisGenerationBar
        hasAnalysis={true}
        jdId={jdId}
        jds={jds}
        onJdChange={onJdChange}
        onGenerate={() => undefined}
        hasTranscript={!!transcript}
        onReplaceTranscript={onReplaceTranscript}
        isPending={isPending}
      />

      {/*
       * Conversation tone / rapport — the REAL model-derived signal parsed from
       * the analysis markdown. Renders only when the analysis carries a parseable
       * `## Conversation Health` section (the component returns null otherwise),
       * so older analyses generated before the signal existed degrade gracefully.
       */}
      <ConversationHealthBar analysis={analysis} />

      {/* Card stack. */}
      <AnalysisCards
        analysis={analysis}
        rankBefore={rankBefore}
        rankAfter={rankAfter}
      />

      {/*
       * B-12: AI rating widget — thumbs up/down on the generated analysis.
       * Right-justified under the card stack, matching AiEvaluationTab /
       * InterviewPrepTab placement. The widget self-guards: it renders nothing
       * when `modelUsed` is null (e.g. the freshly-submitted paste-box path
       * before the complete row carries a model).
       */}
      <div className="flex justify-end px-content-px pb-1 pt-1">
        <AiRatingWidget
          actionType="interview_analysis"
          resourceKey={analysisId ? `interview_analysis:${analysisId}` : ""}
          modelUsed={modelUsed}
        />
      </div>
    </div>
  );
}
